import { useState, useEffect, useRef, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useFormatters } from '../../hooks/useFormatters';

import { Button } from '@/components';
import { useEnterSubmit } from '@/common/hooks';
import cupIcon from '../../assets/cup.png';
import editIcon from '../../assets/editIcon.png';

const COLOR_PALETTE = [
  { name: 'Red', hex: '#ef4444' },
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Orange', hex: '#f97316' },
  { name: 'Rose', hex: '#dc2626' },
  { name: 'Green', hex: '#22c55e' },
  { name: 'Purple', hex: '#a855f7' },
  { name: 'Pink', hex: '#ec4899' },
];

const DEFAULT_PREVIEW_COLUMNS = {
  a: [
    {
      id: 'p1',
      name: 'Raj Savaliya',
      phone: '+91 90233 71032',
      value: 1000,
    },
    {
      id: 'p2',
      name: 'Priya Mehta',
      phone: '+91 98765 43210',
      value: 1500,
    },
  ],
  b: [
    {
      id: 'p3',
      name: 'Raj Savaliya',
      phone: '+91 90233 71032',
      value: 1000,
    },
  ],
};

function BackIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.25}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function MockColumnHeader({ name, color, count, totalValue }) {
  return (
    <div
      className="p-4 rounded-2xl flex flex-col gap-2.5 bg-white border border-[#EBF4EA] shrink-0"
      style={{ borderBottomWidth: '3px', borderBottomColor: '#50AB3D' }}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 overflow-hidden pr-2">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
          <h3 className="font-bold text-[#1F2241] text-base truncate">
            {name}
          </h3>
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full shrink-0"
            style={{ backgroundColor: `${color}1A`, color }}
          >
            {count}
          </span>
        </div>
        <button
          type="button"
          tabIndex={-1}
          className="p-1 rounded shrink-0 pointer-events-none"
        >
          <img src={editIcon} alt="" className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#868686] font-medium">Total Value</span>
        <span className="text-[#1F2241] font-bold tracking-wide">
          {totalValue}
        </span>
      </div>
    </div>
  );
}

function StagePreviewCardBody({ contact }) {
  const { formatCurrency } = useFormatters();
  return (
    <>
      <div className="flex flex-col mb-[14px]">
        <div className="flex justify-between items-start gap-2">
          <p className="font-bold text-[#1F2241] text-base line-clamp-1">
            {contact.name}
          </p>
          <span className="text-xs font-bold text-[#50AB3D] bg-[#EBF4EA] border border-[#50AB3D]/30 px-2 py-0.5 rounded-md shrink-0">
            {formatCurrency(contact.value)}
          </span>
        </div>
        <span className="truncate text-sm text-[#868686]">{contact.phone}</span>
      </div>
      <div className="flex items-center gap-1.5 mb-2.5">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-600 truncate max-w-[90px]">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
          Backend
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-[#868686] bg-white shrink-0">
          02 Jul 2026, 10:30 AM
        </span>
      </div>
      <div className="mt-auto flex items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-primary">J</span>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-[#868686]">Assigned</p>
            <p className="truncate max-w-[100px] font-medium text-[#1F2241]">
              John Uber
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <p className="text-xs text-[#868686]">Primary Source</p>
            <p className="truncate max-w-[80px] font-medium text-[#1F2241]">
              WhatsApp
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function StagePreviewCard({ contact, isDragging = false, onPointerDown }) {
  return (
    <div
      onPointerDown={onPointerDown}
      className={`relative bg-white border rounded-xl p-4 shadow-sm cursor-grab active:cursor-grabbing transition-all select-none ${
        isDragging
          ? 'opacity-50 border-primary ring-1 ring-primary'
          : 'border-[#EBF4EA] hover:border-primary'
      }`}
    >
      <StagePreviewCardBody contact={contact} />
    </div>
  );
}

const PreviewCardGhost = forwardRef(function PreviewCardGhost(
  { contact, width, height, x, y },
  ref,
) {
  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 z-[200] pointer-events-none bg-white border border-primary rounded-xl p-4 shadow-2xl scale-[1.03] opacity-95"
      style={{
        width,
        height,
        transform: `translate3d(${x}px, ${y}px, 0) rotate(0deg)`,
      }}
    >
      <StagePreviewCardBody contact={contact} />
    </div>
  );
});

export function CRMStageModal({
  isOpen,
  onClose,
  onSave,
  initialData = null,
  isSaving = false,
}) {
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6',
    is_won: false,
    is_lost: false,
    send_notification: false,
  });

  // Preview-only drag and drop — same pointer-driven tilt-toward-drag-direction
  // animation as the real kanban board, but moves cards in local state only
  // (no API calls, since this is just a live preview).
  const { formatCurrency } = useFormatters();

  const [previewColumns, setPreviewColumns] = useState(DEFAULT_PREVIEW_COLUMNS);
  const [previewDragVisual, setPreviewDragVisual] = useState(null);
  const [draggedPreviewCardId, setDraggedPreviewCardId] = useState(null);
  const previewDragStateRef = useRef(null);
  const previewDragRAFRef = useRef(null);
  const previewGhostElRef = useRef(null);
  const previewColumnElRefs = useRef({ a: null, b: null });

  const movePreviewCard = (sourceColKey, cardId, targetColKey) => {
    setPreviewColumns((prev) => {
      const sourceList = prev[sourceColKey];
      const cardIndex = sourceList.findIndex((c) => c.id === cardId);
      if (cardIndex === -1) return prev;

      const card = sourceList[cardIndex];
      const nextSourceList = [
        ...sourceList.slice(0, cardIndex),
        ...sourceList.slice(cardIndex + 1),
      ];
      const nextTargetList =
        sourceColKey === targetColKey
          ? [...nextSourceList, card]
          : [...prev[targetColKey], card];

      return {
        ...prev,
        [sourceColKey]: nextSourceList,
        [targetColKey]: nextTargetList,
      };
    });
  };

  const applyPreviewGhostTransform = () => {
    const st = previewDragStateRef.current;
    const el = previewGhostElRef.current;
    if (!st || !el) return;
    el.style.transform = `translate3d(${st.x}px, ${st.y}px, 0) rotate(${st.tilt}deg)`;
  };

  const runPreviewDragFrame = () => {
    const st = previewDragStateRef.current;
    if (!st || !st.moved) {
      previewDragRAFRef.current = null;
      return;
    }

    const rawDeltaX = st.x - st.frameLastX;
    st.frameLastX = st.x;
    st.smoothDeltaX = st.smoothDeltaX + (rawDeltaX - st.smoothDeltaX) * 0.15;

    if (Math.abs(st.smoothDeltaX) > 0.6) {
      const targetTilt = Math.max(-8, Math.min(8, st.smoothDeltaX * 1.6));
      st.tilt = st.tilt + (targetTilt - st.tilt) * 0.15;
      applyPreviewGhostTransform();
    }

    let overColKey = null;
    for (const key of Object.keys(previewColumnElRefs.current)) {
      const zoneEl = previewColumnElRefs.current[key];
      if (!zoneEl) continue;
      const rect = zoneEl.getBoundingClientRect();
      if (
        st.pointerClientX >= rect.left &&
        st.pointerClientX <= rect.right &&
        st.pointerClientY >= rect.top &&
        st.pointerClientY <= rect.bottom
      ) {
        overColKey = key;
        break;
      }
    }
    st.overColKey = overColKey;

    previewDragRAFRef.current = requestAnimationFrame(runPreviewDragFrame);
  };

  const stopPreviewCardDrag = () => {
    window.removeEventListener('pointermove', handlePreviewCardPointerMove);
    window.removeEventListener('pointerup', handlePreviewCardPointerUp);
    if (previewDragRAFRef.current) {
      cancelAnimationFrame(previewDragRAFRef.current);
      previewDragRAFRef.current = null;
    }
    previewDragStateRef.current = null;
    previewGhostElRef.current = null;
    setPreviewDragVisual(null);
    setDraggedPreviewCardId(null);
  };

  const handlePreviewCardPointerMove = (e) => {
    const st = previewDragStateRef.current;
    if (!st) return;

    const dx = e.clientX - st.startClientX;
    const dy = e.clientY - st.startClientY;
    if (!st.moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;

    if (!st.moved) {
      st.moved = true;
      setDraggedPreviewCardId(st.card.id);
      setPreviewDragVisual({
        contact: st.card,
        width: st.width,
        height: st.height,
        x: st.x,
        y: st.y,
      });
      previewDragRAFRef.current = requestAnimationFrame(runPreviewDragFrame);
    }

    st.pointerClientX = e.clientX;
    st.pointerClientY = e.clientY;
    st.x = e.clientX - st.offsetX;
    st.y = e.clientY - st.offsetY;
    applyPreviewGhostTransform();
  };

  const handlePreviewCardPointerUp = () => {
    const st = previewDragStateRef.current;
    if (st?.moved && st.overColKey) {
      movePreviewCard(st.colKey, st.card.id, st.overColKey);
    }
    stopPreviewCardDrag();
  };

  const handlePreviewCardPointerDown = (colKey, card) => (e) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    previewDragStateRef.current = {
      colKey,
      card,
      startClientX: e.clientX,
      startClientY: e.clientY,
      pointerClientX: e.clientX,
      pointerClientY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      x: rect.left,
      y: rect.top,
      frameLastX: rect.left,
      smoothDeltaX: 0,
      tilt: 0,
      moved: false,
      overColKey: null,
    };
    window.addEventListener('pointermove', handlePreviewCardPointerMove);
    window.addEventListener('pointerup', handlePreviewCardPointerUp);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stopPreviewCardDrag, []);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          color: initialData.color || '#3b82f6',
          is_won: initialData.is_won || false,
          is_lost: initialData.is_lost || false,
          send_notification: initialData.send_notification || false,
        });
      } else {
        setFormData({
          name: '',
          color: '#3b82f6',
          is_won: false,
          is_lost: false,
          send_notification: false,
        });
      }
    }
  }, [isOpen, initialData]);

  const isEditing = !!initialData;
  const isWon = formData.is_won;
  const isLost = formData.is_lost;
  const isCustomColor = !COLOR_PALETTE.some(
    (color) => color.hex.toLowerCase() === formData.color.toLowerCase(),
  );

  const statusLabel = isWon
    ? 'Success Stage'
    : isLost
      ? 'Closed-Lost Stage'
      : 'Active Pipeline Stage';
  const statusToneClass = isWon
    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
    : isLost
      ? 'bg-red-500/10 text-red-600 border-red-500/30'
      : 'bg-white text-[#868686] border-[#EBF4EA]';

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  const formRef = useEnterSubmit({
    onSubmit: handleSubmit,
    isPending: isSaving,
  });

  if (!isOpen) return null;

  return (
    <div className="h-full min-h-0 flex flex-col gap-5">
      {/* Header */}
      <div className="bg-white rounded-2xl flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center w-10 h-10 shrink-0 rounded-[8px] bg-white border border-[#50AB3D] text-[#50AB3D] hover:bg-[#EBF4EA] transition-colors focus:outline-none"
          aria-label="Back"
        >
          <BackIcon />
        </button>
        <h1 className="text-xl font-bold text-[#1F2241]">
          {isEditing ? 'Edit Pipeline Stage' : 'Create Pipeline Stage'}
        </h1>
      </div>

      {/* Two-column body — side-by-side columns each fill available height on lg+; on mobile they stack and grow with their content so the page scrolls instead */}
      <div className="lg:flex-1 lg:min-h-0 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Form */}
        <div className="bg-[#F5F5F5] rounded-[12px] p-5 lg:h-full lg:min-h-0 lg:overflow-y-auto custom-scrollbar">
          <form
            id="stage-form"
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-6"
          >
            {/* Stage Name */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-text-secondary">
                Stage Name
              </label>
              <input
                type="text"
                autoFocus
                className="w-full h-11 px-4 rounded-[10px] bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="What should this stage be called?"
                required
              />
            </div>

            {/* Color Chips */}
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium text-text-secondary">
                Choose Stage Colour
              </label>
              <div className="flex flex-wrap gap-3">
                {COLOR_PALETTE.map((color) => {
                  const isSelected = formData.color === color.hex;
                  return (
                    <button
                      key={color.name}
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, color: color.hex })
                      }
                      className={`group relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                        isSelected
                          ? 'ring-2 ring-[#50AB3D] ring-offset-2 ring-offset-[#F5F5F5] scale-110 shadow-lg'
                          : 'hover:ring-2 hover:ring-white hover:ring-offset-1 hover:ring-offset-[#F5F5F5]'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.name}
                    />
                  );
                })}

                {/* Custom Color Picker */}
                <div
                  className={`relative w-9 h-9 rounded-full cursor-pointer flex items-center justify-center transition-all duration-200 hover:scale-110 ${
                    isCustomColor
                      ? 'ring-2 ring-[#50AB3D] ring-offset-2 ring-offset-[#F5F5F5] scale-110 shadow-lg'
                      : 'hover:ring-2 hover:ring-white hover:ring-offset-1 hover:ring-offset-[#F5F5F5]'
                  }`}
                  style={{
                    background: isCustomColor
                      ? formData.color
                      : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  }}
                  title="Custom Color"
                >
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer opacity-0"
                  />
                </div>
              </div>
            </div>

            {/* Status Cards */}
            <div className="flex flex-col gap-3">
              <label className="text-sm font-medium text-text-secondary">
                Stage Behaviour (optional)
              </label>
              <p className="text-xs text-[#868686] -mt-2">
                Does this stage represent the end of a deal?
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Won Card */}
                <div
                  onClick={() =>
                    setFormData({
                      ...formData,
                      is_won: !formData.is_won,
                      is_lost: false,
                    })
                  }
                  className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 ${
                    isWon
                      ? 'bg-emerald-50 border-emerald-500'
                      : 'bg-white border-[#EBF4EA] hover:border-[#868686]'
                  }`}
                >
                  <div className="flex flex-col gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0">
                      <img src={cupIcon} alt="" className="w-7 h-7" />
                    </div>
                    <h4
                      className={`text-lg font-bold ${isWon ? 'text-emerald-600' : 'text-[#1F2241]'}`}
                    >
                      Success Stage
                    </h4>
                    <p className="text-sm text-[#868686]">
                      Mark contacts as Won when they enter this stage.
                    </p>
                  </div>

                  {isWon && (
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Lost Card */}
                <div
                  onClick={() =>
                    setFormData({
                      ...formData,
                      is_lost: !formData.is_lost,
                      is_won: false,
                    })
                  }
                  className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 ${
                    isLost
                      ? 'bg-red-50 border-red-500'
                      : 'bg-white border-[#EBF4EA] hover:border-[#868686]'
                  }`}
                >
                  <div className="flex flex-col gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-red-500 flex items-center justify-center shrink-0">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </div>
                    <h4
                      className={`text-lg font-bold ${isLost ? 'text-red-600' : 'text-[#1F2241]'}`}
                    >
                      Closed-Lost Stage
                    </h4>
                    <p className="text-sm text-[#868686]">
                      Mark contacts as Lost when they enter this stage.
                    </p>
                  </div>

                  {isLost && (
                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Live Preview */}
        <div className="bg-[#F5F5F5] rounded-[12px] p-5 lg:h-full lg:min-h-0 lg:overflow-y-auto custom-scrollbar flex flex-col gap-4">
          <h2 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
            Live Preview
          </h2>

          {/* New stage summary bar — bar itself stays neutral; only the pill on the right reflects won/lost/active */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border bg-white border-[#EBF4EA]">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: formData.color }}
              />
              <span className="text-sm font-bold text-[#1F2241] truncate">
                {formData.name || 'Untitled Stage'}
              </span>
              <span className="text-xs font-bold text-[#868686] bg-white border border-[#EBF4EA] px-2 py-0.5 rounded-full shrink-0">
                01
              </span>
            </div>
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${statusToneClass}`}
            >
              {statusLabel}
            </span>
          </div>

          {/* Mock board preview — same fixed column width as the real board; overflow scrolls instead of shrinking the cards */}
          <div className="flex gap-3 overflow-x-auto overflow-y-hidden custom-scrollbar">
            <div
              ref={(el) => (previewColumnElRefs.current.a = el)}
              className="flex-1 min-w-[260px] flex flex-col gap-3"
            >
              <MockColumnHeader
                name="New Leads"
                color="#50AB3D"
                count={previewColumns.a.length}
                totalValue={formatCurrency(2000)}
              />
              {previewColumns.a.map((card) => (
                <StagePreviewCard
                  key={card.id}
                  contact={card}
                  isDragging={draggedPreviewCardId === card.id}
                  onPointerDown={handlePreviewCardPointerDown('a', card)}
                />
              ))}
            </div>

            <div
              ref={(el) => (previewColumnElRefs.current.b = el)}
              className="flex-1 min-w-[260px] flex flex-col gap-3"
            >
              <MockColumnHeader
                name={formData.name || 'Untitled Stage'}
                color={formData.color}
                count={previewColumns.b.length}
                totalValue={formatCurrency(1000)}
              />
              {previewColumns.b.map((card) => (
                <StagePreviewCard
                  key={card.id}
                  contact={card}
                  isDragging={draggedPreviewCardId === card.id}
                  onPointerDown={handlePreviewCardPointerDown('b', card)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {previewDragVisual &&
        createPortal(
          <PreviewCardGhost
            ref={previewGhostElRef}
            contact={previewDragVisual.contact}
            width={previewDragVisual.width}
            height={previewDragVisual.height}
            x={previewDragVisual.x}
            y={previewDragVisual.y}
          />,
          document.body,
        )}

      {/* Submit: bottom-most, below the aligned columns */}
      <div className="flex justify-end shrink-0">
        <Button
          type="submit"
          variant="primary"
          form="stage-form"
          disabled={isSaving || !formData.name.trim()}
          className="px-6 py-2.5 rounded-xl font-semibold shadow-lg min-w-[160px] gap-2"
        >
          {isSaving ? (
            <svg
              className="animate-spin h-4 w-4 text-white mx-auto"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <>
              Save Changes
              <span aria-hidden="true">→</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
